import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton } from "@alchema/ui";
import { PillTabs } from "~/components/shared/PillTabs";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/interview-prep")({
  beforeLoad: async () => { await requireAuth(); },
  component: InterviewPrepLayout,
  pendingComponent: InterviewPrepLayoutSkeleton,
});

function InterviewPrepLayoutSkeleton() {
  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <Skeleton className="h-8 w-52" />
        <Skeleton className="mt-1 h-4 w-72" />
      </div>
      <div className="mt-6">
        <Skeleton className="h-9 w-72 rounded-lg" />
      </div>
      <div className="mt-6 space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { to: "/interview-prep/", label: () => m.interview_tab_prepare() },
  { to: "/interview-prep/practice", label: () => m.interview_tab_practice() },
  { to: "/interview-prep/question-bank", label: () => m.interview_tab_question_bank() },
];

function InterviewPrepLayout() {
  const tierQuery = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    retry: 0,
    staleTime: 5 * 60 * 1000,
  });

  // Show content immediately — don't block on tier check
  const tier = tierQuery.data;
  const hasPremium = tier && tier.tier !== "free";
  const isChecking = tierQuery.isLoading;

  // Only show upgrade wall when we know for certain the user is free
  // (don't block on the loading state — show content optimistically)
  if (!isChecking && !hasPremium) {
    return (
      <div className="animate-fade-up">
        <div className="page-header">
          <h1 className="page-title">{m.interview_title()}</h1>
        </div>
        <div
          className="mt-8 rounded-xl p-8 text-center animate-fade-up stagger-1"
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <div className="mx-auto max-w-md">
            <div
              className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: "var(--bg-secondary)" }}
            >
              <svg
                className="h-6 w-6"
                style={{ color: "var(--text-muted)" }}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>
              {m.interview_upgrade_title()}
            </h2>
            <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
              {m.interview_upgrade_desc()}
            </p>
            <Link
              to="/settings"
              className="btn btn-primary mt-6"
            >
              {m.interview_upgrade_cta()}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <h1 className="page-title">{m.interview_title()}</h1>
      </div>

      <div className="mt-6 animate-fade-up stagger-1">
        <PillTabs tabs={TABS.map((t) => ({ to: t.to, label: t.label() }))} />
      </div>

      <div className="animate-fade-up stagger-2">
        <Outlet />
      </div>
    </div>
  );
}
